import { API_BASE } from '@/config';

export type AuthEnvironment = 'test' | 'live';

export interface PublicAuthConfig {
  schema_version: 1;
  project_id: string;
  public_token: string;
  environment: AuthEnvironment;
}

// Only what the app reads. Keys the backend adds or retires (the Consumer →
// B2B rollout keys `config_version`/`auth_mode`/`provider` were retired by
// ENG-2371) are ignored, so a backend deploy never breaks the boot.
const requiredKeys = [
  'schema_version',
  'project_id',
  'public_token',
  'environment',
];

function requireString(value: unknown, field: string): string {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`Invalid auth config: ${field}`);
  }
  return value;
}

export function parsePublicAuthConfig(value: unknown): PublicAuthConfig {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Invalid auth config response');
  }

  const config = value as Record<string, unknown>;
  const missing = requiredKeys.filter((key) => !(key in config));
  if (missing.length > 0) {
    throw new Error(
      `Invalid auth config fields: missing ${missing.join(', ')}`,
    );
  }
  if (config.schema_version !== 1) {
    throw new Error('Unsupported auth config schema');
  }
  if (config.environment !== 'test' && config.environment !== 'live') {
    throw new Error('Invalid auth config: environment');
  }

  return {
    schema_version: 1,
    project_id: requireString(config.project_id, 'project_id'),
    public_token: requireString(config.public_token, 'public_token'),
    environment: config.environment,
  };
}

export function authConfigUrl(apiBase = API_BASE): string {
  return `${apiBase.replace(/\/$/, '')}/auth/config`;
}

export async function fetchPublicAuthConfig(
  fetchImpl: typeof fetch = fetch,
): Promise<PublicAuthConfig> {
  const response = await fetchImpl(authConfigUrl(), {
    method: 'GET',
    cache: 'no-store',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Auth config request failed (${response.status})`);
  }
  return parsePublicAuthConfig(await response.json());
}
